import type { CanvasHandle } from '../useEditorCanvas';
import type { CollabRoom } from '@flobi/collab-client';
import type { OpenDocument } from '../useEditorDocument';
import type { PixelSize, Request } from './contracts';
import { eligible } from './resultFill';

/** The paid request may only name a frame after collab has acknowledged its creation. */
export function waitForGenerationSync(room: Pick<CollabRoom, 'status' | 'unsynced' | 'readOnly' | 'on'>, timeoutMs = 15_000): Promise<void> {
  return new Promise((resolve, reject) => {
    const offs: (() => void)[] = [];
    let done = false;
    let timer: ReturnType<typeof setTimeout> | undefined;
    const finish = (error?: Error) => {
      if (done) return;
      done = true; clearTimeout(timer); offs.forEach(off => off());
      if (error) reject(error); else resolve();
    };
    const check = () => {
      if (room.readOnly || room.status === 'refused' || room.status === 'closed') finish(Error('This file cannot receive a generation. Check your editing access.'));
      else if ((room.status === 'synced' || room.status === 'degraded') && room.unsynced === 0) finish();
    };
    offs.push(room.on('unsynced', check), room.on('status', check), room.on('role', check),
      room.on('reset', () => finish(Error('The file changed before the generation started. Try again.'))));
    timer = setTimeout(() => finish(Error('The generation frame has not synced yet. Check your connection and try again.')), timeoutMs);
    check();
  });
}

/** A new canvas frame follows the output ratio, independently of reference geometry. */
export function generationFrameSize(request: Request): PixelSize {
  const custom = request.inputs.customSize as PixelSize | undefined;
  if (custom && Number.isFinite(custom.width) && Number.isFinite(custom.height) && custom.width > 0 && custom.height > 0) return custom;
  const parts = String(request.inputs.aspectRatio || '').split(':').map(Number);
  const reference = request.references[0];
  const ratio = parts.length === 2 && parts[0]! > 0 && parts[1]! > 0 ? parts[0]! / parts[1]!
    : reference && reference.width > 0 && reference.height > 0 ? reference.width / reference.height : 1;
  return ratio >= 1 ? { width: 1024, height: Math.max(1, Math.round(1024 / ratio)) } : { width: Math.max(1, Math.round(1024 * ratio)), height: 1024 };
}

/**
 * Capture a selected fill target, or create a fresh frame only when Generate is pressed.
 * A shape takes one run at a time: Generate while the chosen one is still `generating` makes a
 * new frame beside it (and beside every other box still running) — runs never wait on each other.
 * A new frame also goes beside the `shown` ones (a failed or stopped run's empty frame still on
 * the canvas), never exactly on top of them.
 */
export function prepareGenerationDestination(open: OpenDocument, canvas: Pick<CanvasHandle, 'createGenerationTarget'> & Partial<Pick<CanvasHandle, 'revealGenerationTarget'>>, original: Request, generating: readonly string[] = [], shown: readonly string[] = []) {
  if (original.destination && (original.destination.documentId !== open.documentId || original.destination.pageId !== open.pageId)) throw Error('The generation destination is on another page.');
  const request: Request = original.destination?.targetId && generating.includes(original.destination.targetId) ? { ...original, destination: undefined } : original;
  const targetId = request.destination?.targetId || canvas.createGenerationTarget(generationFrameSize(request), request.kind === 'video' ? 'Generated video' : 'Generated image', [...new Set([...generating, ...shown])]);
  const target = targetId ? open.scene.get(targetId) : null;
  if (!targetId || !target) throw Error(request.destination ? 'The generation destination is unavailable or locked.' : 'Could not create a generation frame. Try again when the canvas is ready.');
  if (!eligible(target) || open.scene.lockedHere(targetId)) throw Error('The generation destination is unavailable or locked.');
  canvas.revealGenerationTarget?.(targetId);
  return {
    targetId, targetName: target.name || 'Shape', createdTarget: !request.destination?.targetId,
    // A newly created preview frame is local placement state, not a prerequisite for
    // authorizing the paid run. The backend still checks this file/page and its payer.
    request: { ...request, destination: { documentId: open.documentId, pageId: open.pageId,
      ...(request.destination?.targetId ? { targetId } : {}) } } satisfies Request,
  };
}
